import type React from 'react';
import { cloneElement, isValidElement, type JSX, lazy, type ReactElement, type ReactNode, Suspense } from 'react';
import { extractTweetIdFromUrl, extractUsernameFromUrl } from '../../lib/twitter/url';
import { cn } from '../../lib/utils';
import { BARE_HANDLE_PATTERN, HANDLE_ONLY_TOKEN_PATTERN, RELATIVE_PROFILE_PATH_PATTERN } from '../../lib/utils/regex';
import type { AvatarProperties } from '../ui/avatar';
import { Avatar } from '../ui/avatar';
import type { StreamdownAnchorTransform } from './StreamdownRenderer';

type ProfileLookupEntry = { username: string; name?: string | null; profileImageUrl?: string | null };
type ProfileLookupMap = Map<string, ProfileLookupEntry>;

type AvatarSizeOption = number | NonNullable<AvatarProperties['size']>;
type TweetLookupEntry = {
  authorUsername?: string | null;
  authorName?: string | null;
  authorProfileImageUrl?: string | null;
};
type TweetLookupMap = Map<string, TweetLookupEntry>;

type AnchorTransformContext = Parameters<StreamdownAnchorTransform>[0];
type AnchorRenderer = AnchorTransformContext['renderAnchor'];
type AnchorChildren = AnchorTransformContext['anchorChildren'];
type AnchorElementProperties = React.AnchorHTMLAttributes<HTMLAnchorElement> & {
  children?: ReactNode;
  'data-preview'?: 'tweet' | 'user';
  'data-tweet-id'?: string;
  'data-username'?: string;
};
type AnchorElement = ReactElement<AnchorElementProperties>;

const ensureAnchorElement = (element: JSX.Element): AnchorElement => {
  if (isValidElement<AnchorElementProperties>(element)) {
    return element;
  }
  return <a href="about:blank">{element}</a>;
};

export interface EntityAnchorTransformOptions {
  avatarClassName?: string | undefined;
  avatarSize?: AvatarSizeOption | undefined;
  handleToTweetId?: Map<string, string> | null | undefined;
  includeTweetPreview?: boolean | undefined;
  includeUserPreview?: boolean | undefined;
  profileLinkTarget?: '_self' | '_blank' | undefined;
  profileLookup?: ProfileLookupMap | null | undefined;
  resolveProfileHref?: ((username: string) => string) | undefined;
  tweetAvatarClassName?: string | undefined;
  tweetAvatarSize?: AvatarSizeOption | undefined;
  tweetLookup?: TweetLookupMap | null | undefined;
}

const LazyTweetPreview = lazy(async () => {
  const { TweetPreview } = await import('../shared/previews/TweetPreview');
  return { default: TweetPreview };
});

const TWEET_LABEL_PATTERN = /tweet:\s*(\d[\d_]*)/i;
const RESIDUAL_PUNCTUATION_PATTERN = /[\s!"#$%&'()*+,\-./:;<=>?@[\\\]^_`{|}~]+/g;

const LazyUserPreview = lazy(async () => {
  const { UserPreview } = await import('../shared/previews/UserPreview');
  return { default: UserPreview };
});

export function createEntityAnchorTransform(options?: EntityAnchorTransformOptions): StreamdownAnchorTransform {
  return createEntityAnchorTransformWithOptions(options);
}

const extractRelativeUsername = (url: string): string | null => {
  if (typeof url !== 'string') {
    return null;
  }
  const trimmed = url.trim();
  if (!trimmed) {
    return null;
  }
  const directMatch = trimmed.match(RELATIVE_PROFILE_PATH_PATTERN);
  if (directMatch?.[1]) {
    return directMatch[1];
  }
  const bareMatch = trimmed.match(BARE_HANDLE_PATTERN);
  return bareMatch?.[1] ?? null;
};

function resolveProfileInfo(
  username: string,
  lookup: ProfileLookupMap | null | undefined
): ProfileLookupEntry | { username: string; name?: string | null; profileImageUrl?: string | null } {
  const normalized = username.toLowerCase();
  if (lookup) {
    return lookup.get(normalized) ?? { username, name: null, profileImageUrl: null };
  }
  return { username, name: null, profileImageUrl: null };
}

function createEntityAnchorTransformWithOptions(options: EntityAnchorTransformOptions = {}): StreamdownAnchorTransform {
  const {
    profileLookup = null,
    resolveProfileHref,
    profileLinkTarget,
    avatarSize = 20,
    avatarClassName,
    includeTweetPreview = true,
    includeUserPreview = true,
    tweetLookup: providedTweetLookup,
    tweetAvatarSize: providedTweetAvatarSize,
    tweetAvatarClassName: providedTweetAvatarClassName,
    handleToTweetId: providedHandleToTweetId,
  } = options;
  const tweetLookup = providedTweetLookup ?? null;
  const tweetAvatarSize = providedTweetAvatarSize ?? avatarSize;
  const tweetAvatarClassName = providedTweetAvatarClassName ?? avatarClassName;
  const handleToTweetId = providedHandleToTweetId ?? null;

  const renderTweetAnchor = ({
    tweetId,
    renderAnchor: render,
    anchorChildren: children,
  }: {
    tweetId: string;
    renderAnchor: AnchorRenderer;
    anchorChildren: AnchorChildren;
  }) => {
    const anchor = ensureAnchorElement(render({ target: '_blank', rel: 'noopener noreferrer' }));
    const anchorProperties = anchor.props;
    const tweetEntry = tweetLookup?.get(tweetId) ?? null;
    const displayName = tweetEntry?.authorName ?? (tweetEntry?.authorUsername ? `@${tweetEntry.authorUsername}` : null);
    const fallbackInitial =
      (tweetEntry?.authorUsername ?? tweetEntry?.authorName ?? tweetId)?.toString().trim().charAt(0).toUpperCase() ||
      '#';
    const avatarForTweet = tweetEntry ? (
      <Avatar
        key="tweet-avatar"
        src={tweetEntry.authorProfileImageUrl ?? undefined}
        alt={displayName ? `${displayName} avatar` : 'Tweet author avatar'}
        size={tweetAvatarSize}
        className={cn('me-1 inline-block align-baseline', tweetAvatarClassName)}
        fallbackText={fallbackInitial}
      />
    ) : null;
    const anchorChildrenContent = children ?? anchorProperties.children ?? null;
    const rawLabel = extractNodeText(anchorChildrenContent).trim();
    const overrideMatch = rawLabel.match(TWEET_LABEL_PATTERN);
    const labelRemainder =
      overrideMatch === null
        ? ''
        : rawLabel.replace(TWEET_LABEL_PATTERN, '').replaceAll(RESIDUAL_PUNCTUATION_PATTERN, '').trim();
    const shouldOverrideLabel = overrideMatch !== null && labelRemainder.length === 0;
    const labelNode = shouldOverrideLabel ? (
      <span className="inline-flex items-center gap-1">
        View tweet{tweetEntry?.authorUsername ? ` by @${tweetEntry.authorUsername}` : ''}
        <span aria-hidden className="text-xs">
          ↗
        </span>
      </span>
    ) : (
      anchorChildrenContent
    );
    const decoratedChildren = avatarForTweet ? (
      <>
        {avatarForTweet}
        <span className="whitespace-normal break-words leading-[inherit]">{labelNode}</span>
      </>
    ) : (
      labelNode
    );
    const normalizedAnchor = cloneElement(anchor, {
      href: `https://x.com/i/status/${tweetId}`,
      'data-preview': 'tweet',
      'data-tweet-id': tweetId,
      className: cn(anchorProperties.className, 'inline max-w-full align-baseline leading-[inherit] break-words'),
      children: decoratedChildren,
    });

    if (!includeTweetPreview) {
      return normalizedAnchor;
    }

    return (
      <Suspense fallback={normalizedAnchor}>
        <LazyTweetPreview tweetId={tweetId} allowNestedHover>
          {normalizedAnchor}
        </LazyTweetPreview>
      </Suspense>
    );
  };

  const renderProfileAnchor = ({
    username,
    renderAnchor: render,
    anchorChildren: children,
  }: {
    username: string;
    renderAnchor: AnchorRenderer;
    anchorChildren: AnchorChildren;
  }) => {
    const profile = resolveProfileInfo(username, profileLookup);
    const href =
      typeof resolveProfileHref === 'function'
        ? resolveProfileHref(profile.username)
        : `https://x.com/${profile.username}`;
    const target = profileLinkTarget ?? (href.startsWith('/') ? '_self' : '_blank');
    const rel = target === '_blank' ? 'noopener noreferrer' : undefined;

    const anchor = ensureAnchorElement(render({ target, rel }));
    const anchorProperties = anchor.props;
    const fallbackInitial =
      profile.username && profile.username.length > 0 ? profile.username.charAt(0).toUpperCase() : '?';
    const anchorChildrenContent = children ?? anchorProperties.children ?? null;
    const avatarNode = (
      <Avatar
        key="avatar"
        src={profile.profileImageUrl ?? undefined}
        alt={profile.name ?? `@${profile.username}`}
        size={avatarSize}
        className={cn('me-1 inline-block align-baseline', avatarClassName)}
        fallbackText={fallbackInitial}
      />
    );
    const decoratedChildren = (
      <>
        {avatarNode}
        <span className="whitespace-normal break-words leading-[inherit]">{anchorChildrenContent}</span>
      </>
    );
    const annotated = cloneElement(anchor, {
      href,
      target,
      rel: rel ?? undefined,
      'data-preview': 'user',
      'data-username': profile.username,
      className: cn(anchorProperties.className, 'inline max-w-full align-baseline leading-[inherit] break-words'),
      children: decoratedChildren,
    });

    if (!includeUserPreview) {
      return annotated;
    }

    return (
      <Suspense fallback={annotated}>
        <LazyUserPreview username={profile.username}>{annotated}</LazyUserPreview>
      </Suspense>
    );
  };

  return ({ url, renderAnchor, anchorChildren }) => {
    let tweetId = extractTweetIdFromUrl(url);
    const missingTweetId = () =>
      // Tests hit this path when sanitized markdown strips the `tweet:` scheme entirely, so we treat null/undefined/blank the same.
      tweetId === null || tweetId === undefined || tweetId === '';
    if (missingTweetId()) {
      // Some sanitized markdown drops the custom scheme from `href`, so we fall back to the raw label when it still
      // follows the `tweet:ID` pattern.
      const labelText = extractNodeText(anchorChildren);
      const labelMatch = labelText.match(TWEET_LABEL_PATTERN);
      if (labelMatch?.[1]) {
        tweetId = labelMatch[1];
      }
    }
    if (missingTweetId() && handleToTweetId && typeof url === 'string') {
      const trimmed = url.trim();
      if (HANDLE_ONLY_TOKEN_PATTERN.test(trimmed)) {
        tweetId = handleToTweetId.get(trimmed) ?? null;
      }
    }
    if (typeof tweetId === 'string' && tweetId.length > 0) {
      return renderTweetAnchor({ tweetId, renderAnchor, anchorChildren });
    }

    const usernameFromUrl = extractUsernameFromUrl(url) ?? extractRelativeUsername(url);
    if (usernameFromUrl) {
      return renderProfileAnchor({ username: usernameFromUrl, renderAnchor, anchorChildren });
    }

    return;
  };
}

export function composeAnchorTransforms(
  ...transforms: Array<StreamdownAnchorTransform | null | undefined>
): StreamdownAnchorTransform | undefined {
  const active = transforms.filter(
    (transform): transform is StreamdownAnchorTransform => typeof transform === 'function'
  );
  if (active.length === 0) {
    return;
  }

  function composedTransform(context: Parameters<StreamdownAnchorTransform>[0]): React.ReactNode {
    for (const transform of active) {
      const result = transform(context);
      if (result !== undefined && result !== null) {
        return result;
      }
    }
    return context.renderAnchor();
  }

  return composedTransform;
}

function extractNodeText(node: ReactNode): string {
  if (node === null || node === undefined) {
    return '';
  }
  if (typeof node === 'string' || typeof node === 'number') {
    return String(node);
  }
  if (Array.isArray(node)) {
    return node.map((child: ReactNode) => extractNodeText(child)).join('');
  }
  if (isValidElement<{ children?: ReactNode }>(node)) {
    return extractNodeText(node.props.children ?? null);
  }
  return '';
}
